import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import {action, computed, reaction, observable} from 'mobx';
import {observer, inject} from 'mobx-react';

import PricingSingle from 'spa/images/icons/pricing_single.svg';
import PricingB2B from 'spa/images/icons/pricing_b2b.svg';
import PricingCouples from 'spa/images/icons/pricing_couples.svg';

import {withConfig, withI18n} from 'spa/Components/Context';
import ProgressiveImage from 'spa/Components/ProgressiveImage';
import autobind from 'autobind-decorator';

@inject(({store: {tracker, products, selection}}) => (
  {
    tracker,
    products,
    selection,
    pricing: products.getSearch({type: '*', city: selection.city_id}),
  }
))
@observer
@withConfig
@withI18n
class PricingTable extends React.Component {
  static propTypes = {
    defaultCity: PropTypes.string.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }),
    products: PropTypes.object.isRequired,
    selection: PropTypes.object.isRequired,
    pricing: PropTypes.object,
    trackingCategory: PropTypes.string.isRequired,
    trackingLocation: PropTypes.string,
  };

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;
    const {selection, products} = this.props;
    products.maybeLoadSearch({type: '*', city: selection.city_id});

    this.productsSearch = this.props.products.getSearch({type: '*', city: selection.city_id});

    this.enablePricingFrom = config.getValue('b2c.enable-pricing-from', i18n.umRegion, selection.city_id);
  }

  componentDidMount() {
    this.listeners = [
      reaction(
        () => this.props.selection.city_id,
        (city_id) => {
          const {products} = this.props;
          /*
           *  Products are always the same, only the price is different, therefore the ids are
           *  always the same. Since the ids are used for keys in the model, it will always return
           *  the same results. That's why we need to clear the searches everytime the selected
           *  city changes.
           */
          products.searches.clear();

          let selectedCity = city_id;

          if (!city_id) {
            const {defaultCity} = this.props;

            selectedCity = defaultCity;
          }

          products.maybeLoadSearch({type: '*', city: selectedCity});
          this.productsSearch = products.getSearch({type: '*', city: selectedCity});
        },
        {
          name: 'track city change',
          fireImmediately: true,
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable productsSearch;

  @autobind
  @action('Click book button - Pricing page')
  onClickBook(event) {
    const {product} = event.currentTarget.dataset;
    const {tracker, selection} = this.props;
    let label = '';
    switch (product) {
    case 'back2back':
      label = 'Back to Back Massage';
      break;
    case 'couples':
      label = 'Couples Massage';
      break;
    default:
      label = 'Single Massage';
    }

    const trackingData = {
      category: this.props.trackingCategory,
      label,
      city: selection && selection.city_id,
    };

    if (this.props.trackingLocation) {
      trackingData.data = {
        page: this.props.trackingLocation,
      };
    }

    tracker.track(`Clicked book now on ${this.props.trackingCategory}`, trackingData);
  }

  @computed get products() {
    const {productsSearch} = this;

    if (!productsSearch) return null;
    const {instances} = productsSearch;
    if (!instances) return null;

    return instances.filter(product => (
      product.type === 'product'
    ));
  }

  @computed get singlePrices() {
    const {products} = this;
    const {i18n} = this.context;

    if (!products) return null;
    return products.map((product, index) => (
      {
        price: product.price,
        label: i18n.__react('%(minutes)s minutes', {minutes: (index + 2) * 30}),
      }
    ));
  }

  @computed get singleTherapistPrices() {
    const {products} = this;
    const {i18n} = this.context;
    if (!products) return null;
    return products.map((product, index) => (
      {
        price: product.price_2 || product.price * 2,
        label: i18n.__react('2 x %(minutes)s minutes', {minutes: (index + 2) * 30}),
      }
    ));
  }

  @computed get doubleTherapistPrices() {
    const {products} = this;
    const {i18n} = this.context;
    if (!products) return null;
    return products.map((product, index) => (
      {
        price: product.price_2 || product.price * 2,
        label: i18n.__react('%(minutes)s minutes', {minutes: (index + 2) * 30}),
      }
    ));
  }

  @computed get pricingSearch() {
    setTimeout(() => this.props.products.maybeLoadSearch({type: '*', city: this.props.selection.city_id}), 0);
    return this.props.products.getSearch({type: '*', city: this.props.selection.city_id});
  }

  render() {
    const {
      cityOptions,
      context,
      enablePricingFrom,
      singlePrices,
      singleTherapistPrices,
      doubleTherapistPrices,
      pricingSearch,
    } = this;
    const {i18n} = context;

    const {currency} = pricingSearch.modelState;

    return (
      <div className="container pricing-table">
        <section className="pricing-table__type-box">
          <div className="pricing-table__type-box-top-section">
            <ProgressiveImage
              alt=""
              disableWebp
              lowResImg={PricingSingle}
              height="59"
              highResImg={PricingSingle}
              highResImg2x={PricingSingle}
              width="57"
            />
            <h3 className="pricing-table__type-box-title">{i18n._x('Single Massage', 'Pricing Table content')}</h3>
            <p className="pricing-table__type-box-description">{i18n._x('1 Therapist for 1 Person', 'Pricing Table content')}</p>
          </div>
          {singlePrices && singlePrices.map((price, index) => (
            <div className="pricing-table__item" key={`simple-${index}`}>
              <div className="row pricing-table__item-row">
                <div className="col-xs-6">
                  {price.label}
                </div>
                <div className="col-xs-6 pricing-table__item-price">
                  {enablePricingFrom && (
                    <span>{i18n._x('from', 'Pricing Table content')} </span>
                  )}
                  {i18n.__react('%(currency)s%(value)s', {
                    currency: currency.textSymbol,
                    value: price.price,
                  })}
                </div>
              </div>
            </div>
          ))}
          <div className="row pricing-table__item-row">
            <Link
              className="button button--primary button--caps button--block pricing-table__item-button"
              onClick={this.onClickBook}
              data-product="single-massage"
              to={`/${i18n.lc}/booking/?people=1&therapists=1&selectedVertical=massage&bookingType=single`}>
              {i18n._x('Book', 'Pricing page book now button')}
            </Link>
          </div>
        </section>
        <section className="pricing-table__type-box">
          <div className="pricing-table__type-box-top-section">
            <ProgressiveImage
              alt=""
              disableWebp
              lowResImg={PricingB2B}
              height="59"
              highResImg={PricingB2B}
              highResImg2x={PricingB2B}
              width="59"
            />
            <h3 className="pricing-table__type-box-title">{i18n._x('Back to Back', 'Pricing Table content')}</h3>
            <p className="pricing-table__type-box-description">{i18n._x('1 Therapist for 2 People', 'Pricing Table content')}</p>
          </div>
          {singleTherapistPrices && singleTherapistPrices.map((price, index) => (
            <div className="pricing-table__item" key={`single-${index}`}>
              <div className="row pricing-table__item-row">
                <div className="col-xs-6">
                  {price.label}
                </div>
                <div className="col-xs-6 pricing-table__item-price">
                  {enablePricingFrom && (
                    <span>{i18n._x('from', 'Pricing Table content')} </span>
                  )}
                  {i18n.__react('%(currency)s%(value)s', {
                    currency: currency.textSymbol,
                    value: price.price,
                  })}
                </div>
              </div>
            </div>
          ))}
          <div className="row pricing-table__item-row">
            <Link
              className="button button--primary button--caps button--block pricing-table__item-button"
              data-product="back2back"
              onClick={this.onClickBook}
              to={`/${i18n.lc}/booking/?people=2&therapists=1&selectedVertical=massage&bookingType=back2back`}>
              {i18n._x('Book', 'Pricing page book now button')}
            </Link>
          </div>
        </section>
        <section className="pricing-table__type-box">
          <div className="pricing-table__type-box-top-section">
            <ProgressiveImage
              alt=""
              disableWebp
              lowResImg={PricingCouples}
              height="58"
              highResImg={PricingCouples}
              highResImg2x={PricingCouples}
              width="56"
            />
            <h3 className="pricing-table__type-box-title">{i18n._x('Couples', 'Pricing Table content')}</h3>
            <p className="pricing-table__type-box-description">{i18n._x('2 Therapists for 2 People', 'Pricing Table content')}</p>
          </div>
          {doubleTherapistPrices && doubleTherapistPrices.map((price, index) => (
            <div className="pricing-table__item" key={`double-${index}`}>
              <div className="row pricing-table__item-row">
                <div className="col-xs-6">
                  {price.label}
                </div>
                <div className="col-xs-6 pricing-table__item-price">
                  {enablePricingFrom && (
                    <span>{i18n._x('from', 'Pricing Table content')} </span>
                  )}
                  {i18n.__react('%(currency)s%(value)s', {
                    currency: currency.textSymbol,
                    value: price.price,
                  })}
                </div>
              </div>
            </div>
          ))}
          <div className="row pricing-table__item-row">
            <Link
              className="button button--primary button--caps button--block pricing-table__item-button"
              data-product="couples"
              onClick={this.onClickBook}
              to={`/${i18n.lc}/booking/?people=2&therapists=2&selectedVertical=massage&bookingType=couples`}>
              {i18n._x('Book', 'Pricing page book now button')}
            </Link>
          </div>
        </section>
      </div>
    );
  }
}

export default PricingTable;



// WEBPACK FOOTER //
// ./spa/Pages/Pricing/PricingTable.jsx